import React, { useState, useRef, useEffect, useCallback } from "react";

import {
  StyledWrapper,
  StyledCommentBody,
  StyledCommentsBodyWrapper,
  StyledReplyActionContainer,
  StyledRepliesWrapper,
  StyledVericalReplyLine,
} from "./comment.style";
import { StyledCommentsWrapper } from "../container/container.style";
import CommentUserInfo from "../commentUserInfo/commentUserInfo.component";
import ActionItem from "../actionItem/actionItem.component";
import InputForm from "../inputForm/inputForm.component";
import useMedia from "../../../hooks/useMedia";

import checkIcon from "../../../assets/icons/check.svg";
import commentIcon from "../../../assets/icons/comment.svg";
import crossIcon from "../../../assets/icons/cross.svg";
import silenceIcon from "../../../assets/icons/silence.svg";
import thumbsupIcon from "../../../assets/icons/thumbsup.svg";
import thumbsupFilledIcon from "../../../assets/icons/thumbsup_filled.svg";
import crossCircleIcon from "../../../assets/icons/cross_circle.svg";
import forwardFilledIcon from "../../../assets/icons/forward_filled.svg";
import forwardIcon from "../../../assets/icons/forward.svg";

const Comment = ({
  data,
  depthLevel,
  handlePostComment,
  replaceComment,
  commentsReadonly,
  passHeightToParent = undefined,
}) => {
  const [showRepyForm, setShowReplyForm] = useState(false);
  const {
    id,
    commenterName,
    commenterPic,
    createdAtTimestamp,
    formattedBody,
    likesCount,
    likeActionUrl,
    likedByMe,
    replies,
  } = data;
  const [repliesData, setRepliesData] = useState(replies);
  const [showReplies, setShowReplies] = useState(false);
  const [repliesHeightOverall, setRepliesHeightOverall] = useState(0);

  const repliesWrapperRef = useRef(null);
  const formRef = useRef(null);
  const { isMobile } = useMedia();
  const levelInDepth = depthLevel || 1;

  const userInfo = {
    commenterName: commenterName,
    commenterPic: commenterPic,
    createdAtTimestamp: createdAtTimestamp,
  };

  useEffect(() => {
    setRepliesData(replies);
  }, [replies]);

  const concatedBody = () => (
    <StyledCommentBody>
      <span>{data.adminStatuses ?? null}</span>
      <div dangerouslySetInnerHTML={{ __html: formattedBody }}></div>
    </StyledCommentBody>
  );

  const replaceReply = (comment) => {
    const index = repliesData.findIndex((c) => c.id === comment.id);
    const newComments = [...repliesData];
    newComments[index] = comment;
    setRepliesData(newComments);
  };

  const handleReply = () => setShowReplyForm((prev) => !prev);
  const handleTriggerReplies = () => setShowReplies((prev) => !prev);
  const repliesEligibleToDisplay = repliesData.length > 0 && showReplies;
  const repliesHeight = repliesWrapperRef?.current?.scrollHeight;
  const formHeight = formRef?.current?.scrollHeight;

  useEffect(() => {
    setRepliesHeightOverall(repliesHeight + formHeight);
  }, [showReplies, showRepyForm]);

  useEffect(() => {
    if (passHeightToParent) passHeightToParent(repliesHeightOverall);
  }, [repliesHeightOverall, passHeightToParent]);

  const handlePassHeightToParent = useCallback(
    (height) => {
      setRepliesHeightOverall((prev) => prev + height);
    },
    [showReplies, showRepyForm],
  );

  const renderShowRepliesButton = () => {
    if (!repliesData.length) return <div></div>;
    return (
      <StyledReplyActionContainer showreplies={showReplies ? 1 : 0}>
        <div>
          <div></div>
          <div onClick={handleTriggerReplies}>
            {showReplies ? (
              <>
                Hide replies
                <img src={forwardIcon} alt="" />
              </>
            ) : (
              <>
                Show replies
                <img src={forwardFilledIcon} alt="" />
              </>
            )}
          </div>
        </div>
      </StyledReplyActionContainer>
    );
  };

  return (
    <>
      <StyledWrapper
        depthlevel={levelInDepth - 1}
        showreplies={showReplies ? 1 : 0}
      >
        <div>
          <CommentUserInfo data={userInfo} />
          {!isMobile && renderShowRepliesButton()}
        </div>
        <div>
          {isMobile && repliesData.length > 0 && (
            <StyledVericalReplyLine></StyledVericalReplyLine>
          )}
          <StyledCommentsBodyWrapper showreplies={showReplies ? 1 : 0}>
            {concatedBody()}
            <div>
              <div>
                <ActionItem
                  url={likeActionUrl}
                  icon={likedByMe ? thumbsupFilledIcon : thumbsupIcon}
                  text={likesCount}
                  actionCallback={replaceComment}
                />
                {!commentsReadonly && levelInDepth < 4 && (
                  <ActionItem
                    callback={handleReply}
                    icon={commentIcon}
                    text="Reply"
                  />
                )}
                {data.deleteCommentUrl && (
                  <ActionItem
                    url={data.deleteCommentUrl}
                    icon={crossCircleIcon}
                    text="Delete"
                    actionCallback={replaceComment}
                  />
                )}
              </div>
              <div>
                {data.approveCommentUrl && (
                  <ActionItem
                    url={data.approveCommentUrl}
                    icon={checkIcon}
                    text="Approve"
                    actionCallback={replaceComment}
                  />
                )}
                {data.rejectCommentUrl && (
                  <ActionItem
                    url={data.rejectCommentUrl}
                    icon={crossIcon}
                    text="Reject"
                    actionCallback={replaceComment}
                  />
                )}
                {data.silenceAuthorUrl && (
                  <ActionItem
                    url={data.silenceAuthorUrl}
                    icon={silenceIcon}
                    text="Silence"
                    actionCallback={replaceComment}
                  />
                )}
              </div>
            </div>
          </StyledCommentsBodyWrapper>
        </div>
        {isMobile && renderShowRepliesButton()}
      </StyledWrapper>
      {!commentsReadonly && (
        <InputForm
          ref={formRef}
          handlePostComment={handlePostComment}
          parentId={id}
          intentLevel={levelInDepth}
          setShowReplyForm={setShowReplyForm}
          showForm={showRepyForm}
          setShowReplies={setShowReplies}
        />
      )}
      {repliesData.length > 0 && (
        <StyledRepliesWrapper
          style={{
            maxHeight: repliesEligibleToDisplay
              ? `${repliesHeightOverall}px`
              : 0,
          }}
          showreplies={repliesEligibleToDisplay ? 1 : 0}
        >
          <StyledCommentsWrapper ref={repliesWrapperRef}>
            {repliesData.map((reply) => (
              <Comment
                key={reply.id}
                data={reply}
                depthLevel={levelInDepth + 1}
                handlePostComment={handlePostComment}
                replaceComment={replaceReply}
                passHeightToParent={handlePassHeightToParent}
              />
            ))}
          </StyledCommentsWrapper>
        </StyledRepliesWrapper>
      )}
    </>
  );
};

export default Comment;
